import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import { useFormContext } from 'react-hook-form';
import { Button } from '@repo/ui';
import { colorPalette } from '@repo/mui-config/color-palette';
import type { Currency } from '@repo/shared/types';
import { convertNumberToLocaleMoneyFormat } from '@repo/shared/helpers';

import { AdvanceAmountEditPreviewWrapper } from '@/components/loan-process/loan-stages/stage-2/agreement-and-approvals/summary/advance-amount-edit-preview-wrapper';

import { useAgreementAndApprovalsContext } from '../context/use-agreement-and-approvals-context';
import type { AgreementAndApprovalsFields } from '../agreement-and-approvals.schema';

interface AgreementAndApprovalsSummaryPreviewProps {
  currency: Currency;
  withEditMode: boolean;
}
export function AdvancedAmountPreview({
  currency,
  withEditMode,
}: AgreementAndApprovalsSummaryPreviewProps) {
  const { t } = useTranslation();
  const { watch } = useFormContext<AgreementAndApprovalsFields>();
  const { setIsEditMode } = useAgreementAndApprovalsContext();
  const originalAmount = watch('originalAmount');

  return (
    <AdvanceAmountEditPreviewWrapper testId="advanced-amount-preview-mode">
      <Stack
        alignItems="center"
        direction={{ sm: 'row', xs: 'column' }}
        gap={1}
        justifyContent="space-between"
      >
        <Typography fontWeight={700}>
          {t('loanProcess.loanSteps.agreementAndApprovals.summary.advancedAmount.title')}
        </Typography>
        {withEditMode ? (
          <Button
            data-testid="advanced-amount-preview-edit-button"
            onClick={() => {
              setIsEditMode(true);
            }}
            sx={{ textTransform: 'none' }}
            variant="text"
          >
            <Stack alignItems="center" direction="row" gap={1}>
              <Typography fontWeight={700} variant="subtitle2">
                {t('loanProcess.loanSteps.agreementAndApprovals.summary.advancedAmount.button')}
              </Typography>
              <ModeEditOutlineOutlinedIcon
                sx={{
                  width: '12px',
                  height: '12px',
                  color: colorPalette.ink.main,
                }}
              />
            </Stack>
          </Button>
        ) : null}
      </Stack>
      <Typography component="p" fontWeight={900} sx={{ paddingY: 2.5 }} variant="h1">
        {convertNumberToLocaleMoneyFormat(originalAmount)}
        <Typography component="span" fontWeight={900} sx={{ marginLeft: 1 }} variant="h2">
          {currency}
        </Typography>
      </Typography>
    </AdvanceAmountEditPreviewWrapper>
  );
}
